import React, {
    createContext, useContext, useState, useEffect, useRef, useCallback,
} from 'react';

const API = '/api/support';
const STORAGE_KEY = 'ps_support_session';
const LAST_ID_KEY = 'ps_support_last_id';

const SupportContext = createContext(null);

function generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
}

function loadOrCreateSession() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.session_id && parsed?.user_id) return parsed;
        }
    } catch (_) { }
    const fresh = {
        session_id: generateSessionId(),
        user_id: 'user_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh)); } catch (_) { }
    return fresh;
}

export function SupportProvider({ children }) {
    const [session] = useState(loadOrCreateSession);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [unread, setUnread] = useState(0);
    const [connected, setConnected] = useState(false);
    const [sending, setSending] = useState(false);

    const esRef = useRef(null);
    const isOpenRef = useRef(isOpen);
    const seenIdsRef = useRef(new Set());
    const bootstrappedRef = useRef(false);

    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

    // ─── Единая точка добавления сообщений ───
    const upsertMessages = useCallback((incoming, { markUnreadIfClosed = false } = {}) => {
        if (!incoming?.length) return;
        setMessages((prev) => {
            const map = new Map(prev.map((m) => [m.id, m]));
            let addedOps = 0;
            let addedAny = 0;
            for (const m of incoming) {
                if (seenIdsRef.current.has(m.id)) continue;
                if (m.role === 'client' && m.temp_id) {
                    // оптимистичное сообщение уходит — оставляем реальное
                    map.delete(m.temp_id);
                    seenIdsRef.current.add(m.temp_id);
                }
                map.set(m.id, m);
                seenIdsRef.current.add(m.id);
                addedAny++;
                if (m.role === 'operator') addedOps++;
            }
            if (addedAny > 0) {
                let maxId = 0;
                for (const k of map.keys()) {
                    const n = Number(k);
                    if (!Number.isNaN(n) && n > maxId) maxId = n;
                }
                if (maxId > 0) {
                    try { localStorage.setItem(LAST_ID_KEY, String(maxId)); } catch (_) { }
                }
            }
            if (markUnreadIfClosed && !isOpenRef.current && addedOps > 0) {
                setUnread((n) => n + addedOps);
            }
            return Array.from(map.values()).sort((a, b) => a.id - b.id);
        });
    }, []);

    // ─── Bootstrap: REST → SSE ───
    useEffect(() => {
        if (bootstrappedRef.current) return;
        bootstrappedRef.current = true;

        let stop = false;
        let retryDelay = 1000;
        let retryTimer = null;
        let es = null;

        const fetchHistory = async () => {
            try {
                const r = await fetch(
                    `${API}/messages?session_id=${encodeURIComponent(session.session_id)}&since_id=0`
                );
                if (!r.ok) return;
                const data = await r.json();
                const list = data.messages || [];
                if (stop) return;
                upsertMessages(list, { markUnreadIfClosed: false });
                if (!isOpenRef.current) {
                    const ops = list.filter((m) => m.role === 'operator').length;
                    if (ops) setUnread(ops);
                }
                console.log('[Support] history loaded:', list.length);
            } catch (e) {
                console.warn('[Support] history fetch failed:', e);
            }
        };

        const connectSSE = () => {
            if (stop) return;
            const sinceId = Number(localStorage.getItem(LAST_ID_KEY) || 0);
            const url = `${API}/stream?session_id=${encodeURIComponent(session.session_id)}&since_id=${sinceId}`;
            es = new EventSource(url);
            esRef.current = es;

            es.addEventListener('open', () => {
                setConnected(true);
                retryDelay = 1000;
                console.log('[Support] SSE connected');
            });

            es.addEventListener('initial', (e) => {
                try {
                    const data = JSON.parse(e.data);
                    upsertMessages(data.messages || [], { markUnreadIfClosed: true });
                } catch (_) { }
            });

            es.addEventListener('message', (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    upsertMessages([msg], { markUnreadIfClosed: true });
                } catch (_) { }
            });

            es.addEventListener('error', () => {
                setConnected(false);
                try { es.close(); } catch (_) { }
                if (stop) return;
                retryTimer = setTimeout(() => {
                    retryDelay = Math.min(retryDelay * 1.8, 30000);
                    connectSSE();
                }, retryDelay);
            });
        };

        (async () => {
            await fetchHistory();
            if (!stop) connectSSE();
        })();

        return () => {
            stop = true;
            if (retryTimer) clearTimeout(retryTimer);
            try { es?.close(); } catch (_) { }
            esRef.current = null;
        };
    }, [session.session_id, upsertMessages]);

    // ─── Polling-fallback, если SSE не подключён ───
    useEffect(() => {
        if (connected) return;
        let stopped = false;
        const tick = async () => {
            if (stopped) return;
            try {
                const sinceId = Number(localStorage.getItem(LAST_ID_KEY) || 0);
                const r = await fetch(
                    `${API}/messages?session_id=${encodeURIComponent(session.session_id)}&since_id=${sinceId}`
                );
                if (r.ok) {
                    const data = await r.json();
                    if (data.messages?.length) {
                        upsertMessages(data.messages, { markUnreadIfClosed: true });
                    }
                }
            } catch (_) { }
            if (!stopped) setTimeout(tick, 15000);
        };
        const t = setTimeout(tick, 15000);
        return () => { stopped = true; clearTimeout(t); };
    }, [connected, session.session_id, upsertMessages]);

    // ─── Отправка сообщения ───
    const sendMessage = useCallback(async (text) => {
        const t = (text || '').trim();
        if (!t || sending) return;
        setSending(true);

        const tempId = 'tmp_' + Date.now();
        const optimistic = {
            id: tempId,
            temp_id: tempId,
            role: 'client',
            text: t,
            created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, optimistic]);

        const isFirst = messages.filter((m) => m.role === 'client' && !m.temp_id).length === 0;
        const url = isFirst ? `${API}/start` : `${API}/message`;
        const body = isFirst
            ? { session_id: session.session_id, user_id: session.user_id, first_message: t }
            : { session_id: session.session_id, user_id: session.user_id, text: t };

        try {
            const r = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
        } catch (e) {
            setMessages((prev) => prev.filter((m) => m.id !== tempId));
            setMessages((prev) => [
                ...prev,
                {
                    id: 'err_' + Date.now(),
                    role: 'system',
                    text: '⚠️ Сообщение не отправилось. Попробуйте ещё раз.',
                },
            ]);
        } finally {
            setSending(false);
        }
    }, [messages, sending, session]);

    const openChat = useCallback(() => {
        setIsOpen(true);
        setUnread(0);
    }, []);
    const closeChat = useCallback(() => setIsOpen(false), []);

    const value = {
        session, messages, unread, connected, sending,
        isOpen, openChat, closeChat, sendMessage,
    };

    return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

export function useSupport() {
    const ctx = useContext(SupportContext);
    if (!ctx) throw new Error('useSupport must be used within SupportProvider');
    return ctx;
}